import {useEffect} from "react";

class TitleManager {
  private prefix: string | null = null;
  private appName: string | null = null;
  private postFixQueue: string[] = []; // show last added part first
  private batchTimeoutId: ReturnType<typeof setTimeout> | null = null;

  buildTitle() {
    if (!this.batchTimeoutId) {
      this.batchTimeoutId = setTimeout(() => {
        const title = [this.appName ?? "Codecks", ...this.postFixQueue].join(" – ");
        const fullTitle = this.prefix ? `${this.prefix} ${title}` : title;
        document.title = fullTitle;
        this.batchTimeoutId = null;
      }, 16);
    }
  }

  setPrefix(prefix: string | null) {
    this.prefix = prefix;
    this.buildTitle();
  }

  setAppName(appName: string | null) {
    this.appName = appName;
    this.buildTitle();
  }

  addPostFix(postFix: string) {
    this.postFixQueue.unshift(postFix);
    this.buildTitle();
    return () => {
      this.postFixQueue.splice(this.postFixQueue.indexOf(postFix), 1);
      this.buildTitle();
    };
  }
}

const titleManager = new TitleManager();

export const useTitlePrefix = (prefix: string | null) => {
  useEffect(() => {
    if (!prefix) return;
    titleManager.setPrefix(prefix);
    return () => titleManager.setPrefix(null);
  }, [prefix]);
};

export const useTitleAppName = (appName: string | null) => {
  useEffect(() => {
    if (!appName) return;
    titleManager.setAppName(appName);
    return () => titleManager.setAppName(null);
  }, [appName]);
};

export const useTitlePostFix = (postFix: string | null) => {
  useEffect(() => {
    if (!postFix) return;
    return titleManager.addPostFix(postFix);
  }, [postFix]);
};

class MetaDescriptionManager {
  private stack: string[] = [];
  private batchTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private descTag: HTMLMetaElement | null = null;

  private ensureDescTag() {
    if (!this.descTag) {
      const exist = document.querySelector('meta[name="description"]');
      if (exist) {
        this.descTag = exist as HTMLMetaElement;
      } else {
        this.descTag = document.createElement("meta");
        this.descTag.name = "description";
        document.getElementsByTagName("head")[0].appendChild(this.descTag);
      }
    }
    return this.descTag;
  }

  addValue(value: string) {
    this.stack.unshift(value);
    this.buildTags();
    return () => {
      this.stack.splice(this.stack.indexOf(value), 1);
      this.buildTags();
    };
  }

  buildTags() {
    if (!this.batchTimeoutId) {
      this.batchTimeoutId = setTimeout(() => {
        const descTag = this.ensureDescTag();
        descTag.content = this.stack[0];
      }, 16);
    }
  }
}

const descManager = new MetaDescriptionManager();

export const useMetaDescription = (content: string | null) => {
  useEffect(() => {
    if (!content) return;
    return descManager.addValue(content);
  }, [content]);
};
